import { Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { CellRenderer, CellRendererParams } from 'app/shared/components/smart-grid/core/interfaces/cell-renderer.interface';
import { DocumentNavigationComponent } from 'app/common/components/document-navigation/document-navigation.component';
import { UnifiedOrderBadgeComponent } from 'app/shared/components/unified-order-badge/unified-order-badge.component';
import { OrderAttributes } from '@albatrosdeveloper/ave-models-package';
import OrderSourceTransactionAttributes from '@albatrosdeveloper/ave-models-package/lib/schemas/process/order/order-source-transaction/order-source-transaction.entity';

/**
 * Order Document Cell Renderer
 *
 * Wraps the DocumentNavigationComponent for displaying order serial numbers
 * with navigation functionality in smart-grid tables.
 * Includes UnifiedOrderBadge for unified orders.
 *
 * @example
 * ```typescript
 * {
 *   field: 'serial',
 *   headerName: 'Nro. Documento',
 *   cellRenderer: OrderDocumentCellRendererComponent,
 *   minWidth: 150
 * }
 * ```
 */
@Component({
  selector: 'app-order-document-cell-renderer',
  standalone: true,
  imports: [CommonModule, DocumentNavigationComponent, UnifiedOrderBadgeComponent],
  template: `
    <div class="flex items-center gap-2">
      <p class="table-text-m whitespace-nowrap text-sm">
        <b>
          <app-document-navigation
            [documentType]="documentType"
            [documentSerial]="documentSerial"
            [sourceTransactions]="sourceTransactions"
            [displayText]="displayText"
            [showIcon]="true">
          </app-document-navigation>
        </b>
      </p>

      <app-unified-order-badge [order]="orderData"></app-unified-order-badge>
    </div>
  `,
})
export class OrderDocumentCellRendererComponent implements CellRenderer<OrderAttributes> {
  @Input() params!: CellRendererParams<OrderAttributes>;

  get documentType(): string {
    return this.params.data?.type?.code ?? '';
  }

  get documentSerial(): string | undefined {
    return this.params.data?.serial;
  }

  get sourceTransactions(): OrderSourceTransactionAttributes[] | undefined {
    return this.params.data?.sourceTransactions;
  }

  get displayText(): string {
    const serial = this.params.data?.serial;
    return serial ?? '-';
  }

  /**
   * Order data for the unified order badge
   */
  get orderData(): Partial<OrderAttributes> {
    return {
      serial: this.documentSerial,
      sourceTransactions: this.sourceTransactions,
      operationType: this.params.data?.operationType,
      isUnified: this.params.data?.isUnified
    };
  }
}
